/**
 * Before/After Cases (Provider) — TCPN PHASE 10-B2B B2D
 * =====================================================
 * Trust-first CRUD for provider-managed before/after cases.
 *
 * Backend (JWT-bound):
 *   GET    /api/clinic/before-after-cases[?state=&treatment_slug=]
 *   POST   /api/clinic/before-after-cases
 *   PATCH  /api/clinic/before-after-cases/{case_id}     (caption only, draft/rejected)
 *   POST   /api/clinic/before-after-cases/{case_id}/submit
 *   DELETE /api/clinic/before-after-cases/{case_id}     (soft archive)
 *   POST   /api/before-after/consent/patient-self        (patient logs in & signs)
 *   POST   /api/before-after/consent/provider-attested   (clinic uploads release form)
 *   POST   /api/before-after/consent/{id}/withdraw
 *
 * Media uploads use the existing /api/clinic/media SSOT (context_type=clinic_profile|treatment).
 * NO public engagement metrics, NO social mechanics, NO viral feed.
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { dateInputLang } from '../../../utils/dateFormat';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../components/ui/button';
import { Card, CardContent } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Badge } from '../../../components/ui/badge';
import { Toaster, toast } from 'sonner';
import {
  ArrowLeft, Plus, Upload, Loader2, Image as ImageIcon, X,
  ShieldCheck, ShieldAlert, ShieldQuestion, Archive, Send, CheckCircle2
} from 'lucide-react';
import api from '../../../utils/api';
import { getActiveWorkspaceClinicId } from '../../../utils/activeWorkspace';
import AuthMediaThumb from '../../../components/AuthMediaThumb';
import SendConsentRequestModal from './SendConsentRequestModal';

// Canonical state enum → i18n key + visual class. Backend enum values
// (draft/pending_review/approved/rejected/archived) remain unchanged;
// only visual display chrome is localized.
const STATE_BADGE = {
  draft: { key: 'draft', cls: 'bg-slate-100 text-slate-700' },
  pending_review: { key: 'pending_review', cls: 'bg-amber-100 text-amber-800' },
  approved: { key: 'approved', cls: 'bg-emerald-100 text-emerald-800' },
  rejected: { key: 'rejected', cls: 'bg-rose-100 text-rose-800' },
  archived: { key: 'archived', cls: 'bg-slate-200 text-slate-600' },
};

const REJECTION_KEY = [
  'consent_invalid',
  'identifying_info_visible',
  'prohibited_text',
  'quality_issues',
  'other',
];

const BeforeAfterCasesPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [cases, setCases] = useState([]);
  const [treatments, setTreatments] = useState([]);
  const [stateFilter, setStateFilter] = useState('all');

  const [createOpen, setCreateOpen] = useState(false);
  const [step, setStep] = useState(1);
  // TREATX-L1E: consent-request lifecycle initiator
  const [sendReqOpen, setSendReqOpen] = useState(false);
  const [form, setForm] = useState({
    treatment_slug: '',
    consent_path: 'patient_self',
    patient_consent_id: '',
    before_media_id: '',
    after_media_id: '',
    caption: '',
    patient_email: '',
    patient_name: '',
    typed_signature: '',
    attestation_doc_media_id: '',
    // TREATX-L1E — regulatory dates + provider attestations
    procedure_date: '',
    before_captured_at: '',
    after_captured_at: '',
    same_environment_confirmed: false,
    no_retouch_confirmed: false,
    no_privacy_violation_confirmed: false,
    no_incentive_offered_confirmed: false,
  });
  const [busy, setBusy] = useState(false);
  const beforeRef = useRef(null);
  const afterRef = useRef(null);
  const attestRef = useRef(null);

  const stateLabel = (s) => t(`provider.beforeAfter.states.${STATE_BADGE[s]?.key || s}`, { defaultValue: s });
  const rejectionLabel = (r) => t(`provider.beforeAfter.rejectionReasons.${r}`, { defaultValue: r });
  const missingFieldLabel = (id) => t(`provider.beforeAfter.missingFields.${id}`, { defaultValue: id });

  const fetchCases = useCallback(async () => {
    setLoading(true);
    try {
      const params = stateFilter !== 'all' ? { state: stateFilter } : {};
      const res = await api.get('/clinic/before-after-cases', { params });
      setCases(res.data?.cases || []);
    } catch (err) {
      toast.error(err?.response?.data?.message || t('provider.beforeAfter.toasts.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [stateFilter, t]);

  const fetchTreatments = useCallback(async () => {
    try {
      const res = await api.get('/clinic/treatments');
      setTreatments(res.data?.treatments || []);
    } catch (_) {
      setTreatments([]);
    }
  }, []);

  useEffect(() => { fetchCases(); }, [fetchCases]);
  useEffect(() => { fetchTreatments(); }, [fetchTreatments]);

  const uploadFile = async (file, contextType, contextId) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('context_type', contextType);
    fd.append('context_id', contextId);
    const res = await api.post('/clinic/media', fd, {
      headers: { 'Content-Type': undefined },
    });
    if (!res.data?.media_id) throw new Error(res.data?.message || 'Upload failed');
    return res.data.media_id;
  };

  const onFile = async (e, slot) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!form.treatment_slug && (slot === 'before' || slot === 'after')) {
      toast.error(t('provider.beforeAfter.toasts.treatmentFirst'));
      return;
    }
    const clinicId = getActiveWorkspaceClinicId();
    if (!clinicId) {
      toast.error(t('provider.beforeAfter.toasts.noWorkspace'));
      return;
    }
    setBusy(true);
    try {
      const ctxType = slot === 'attest' ? 'release_document' : 'treatment';
      const ctxId = slot === 'attest' ? clinicId : `${clinicId}:${form.treatment_slug}`;
      const mediaId = await uploadFile(file, ctxType, ctxId);
      const fieldMap = { before: 'before_media_id', after: 'after_media_id', attest: 'attestation_doc_media_id' };
      setForm((f) => ({ ...f, [fieldMap[slot]]: mediaId }));
      const slotLabel = slot === 'attest'
        ? t('provider.beforeAfter.toasts.releaseDocUploaded')
        : t('provider.beforeAfter.toasts.slotUploaded', { slot: slot === 'before' ? t('provider.beforeAfter.alt.before') : t('provider.beforeAfter.alt.after') });
      toast.success(slotLabel);
    } catch (err) {
      const status = err?.response?.status;
      const data = err?.response?.data;
      let detail;
      if (data?.message) {
        detail = data.message;
      } else if (data?.error) {
        detail = data.error;
      } else if (Array.isArray(data?.detail)) {
        detail = data.detail
          .map((d) => `${(d.loc || []).slice(-1)[0] || ''}: ${d.msg || d.type || 'invalid'}`)
          .join('; ');
      } else if (typeof data?.detail === 'string') {
        detail = data.detail;
      } else {
        detail = err?.message || 'Upload failed';
      }
      toast.error(status
        ? t('provider.beforeAfter.toasts.uploadFailedStatus', { status, detail })
        : t('provider.beforeAfter.toasts.uploadFailed', { detail }));
    } finally {
      setBusy(false);
    }
  };

  const recordProviderAttestedConsent = async () => {
    const missing = [];
    if (!form.patient_name)             missing.push('patient_name');
    if (!form.patient_email)            missing.push('patient_email');
    if (!form.typed_signature)          missing.push('typed_signature');
    if (!form.attestation_doc_media_id) missing.push('release_document');
    if (missing.length) {
      toast.error(t('provider.beforeAfter.toasts.attestationMissing', { fields: missing.map(missingFieldLabel).join(', ') }));
      return null;
    }
    try {
      const res = await api.post('/before-after/consent/provider-attested', {
        patient_email: form.patient_email,
        patient_name: form.patient_name,
        attestation_doc_media_id: form.attestation_doc_media_id,
        typed_signature_by_clinic_admin: form.typed_signature,
        language: 'en',
      });
      if (!res.data?.consent_id) throw new Error('No consent_id returned');
      setForm((f) => ({ ...f, patient_consent_id: res.data.consent_id }));
      toast.success(t('provider.beforeAfter.toasts.attestationRecorded'));
      return res.data.consent_id;
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || t('provider.beforeAfter.toasts.attestationFailed'));
      return null;
    }
  };

  const submitCase = async () => {
    if (!form.treatment_slug || !form.before_media_id || !form.after_media_id || !form.patient_consent_id) {
      toast.error(t('provider.beforeAfter.toasts.requiredFields'));
      return;
    }
    // TREATX-L1E: enforce regulatory metadata + all four attestations
    // before submit. Backend re-validates; this is the friendly UX gate.
    const l1eMissing = [];
    if (!form.procedure_date) l1eMissing.push(t('provider.beforeAfter.l1e.fields.procedureDate'));
    if (!form.before_captured_at) l1eMissing.push(t('provider.beforeAfter.l1e.fields.beforeCapturedAt'));
    if (!form.after_captured_at) l1eMissing.push(t('provider.beforeAfter.l1e.fields.afterCapturedAt'));
    if (!form.same_environment_confirmed) l1eMissing.push(t('provider.beforeAfter.l1e.attestations.sameEnvironment'));
    if (!form.no_retouch_confirmed) l1eMissing.push(t('provider.beforeAfter.l1e.attestations.noRetouch'));
    if (!form.no_privacy_violation_confirmed) l1eMissing.push(t('provider.beforeAfter.l1e.attestations.noPrivacyViolation'));
    if (!form.no_incentive_offered_confirmed) l1eMissing.push(t('provider.beforeAfter.l1e.attestations.noIncentive'));
    if (l1eMissing.length) {
      toast.error(t('provider.beforeAfter.l1e.errors.missing', { fields: l1eMissing.join(', ') }));
      return;
    }
    setBusy(true);
    try {
      const res = await api.post('/clinic/before-after-cases', {
        treatment_slug: form.treatment_slug,
        before_media_id: form.before_media_id,
        after_media_id: form.after_media_id,
        patient_consent_id: form.patient_consent_id,
        consent_path: form.consent_path,
        caption: form.caption || null,
        submit_for_review: true,
        procedure_date: form.procedure_date,
        before_captured_at: form.before_captured_at,
        after_captured_at: form.after_captured_at,
        same_environment_confirmed: form.same_environment_confirmed,
        no_retouch_confirmed: form.no_retouch_confirmed,
        no_privacy_violation_confirmed: form.no_privacy_violation_confirmed,
        no_incentive_offered_confirmed: form.no_incentive_offered_confirmed,
      });
      if (!res.data?.success) throw new Error(res.data?.message || 'Create failed');
      toast.success(t('provider.beforeAfter.toasts.submitted'));
      setCreateOpen(false);
      setStep(1);
      setForm({
        treatment_slug: '', consent_path: 'patient_self', patient_consent_id: '',
        before_media_id: '', after_media_id: '', caption: '',
        patient_email: '', patient_name: '', typed_signature: '', attestation_doc_media_id: '',
        procedure_date: '', before_captured_at: '', after_captured_at: '',
        same_environment_confirmed: false, no_retouch_confirmed: false,
        no_privacy_violation_confirmed: false, no_incentive_offered_confirmed: false,
      });
      fetchCases();
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || t('provider.beforeAfter.toasts.submitFailed'));
    } finally {
      setBusy(false);
    }
  };

  const archiveCase = async (caseId) => {
    if (!window.confirm(t('provider.beforeAfter.confirms.archive'))) return;
    try {
      await api.delete(`/clinic/before-after-cases/${caseId}`);
      toast.success(t('provider.beforeAfter.toasts.archived'));
      fetchCases();
    } catch (err) {
      toast.error(err?.response?.data?.message || t('provider.beforeAfter.toasts.archiveFailed'));
    }
  };

  const resubmit = async (caseId) => {
    try {
      await api.post(`/clinic/before-after-cases/${caseId}/submit`);
      toast.success(t('provider.beforeAfter.toasts.resubmitted'));
      fetchCases();
    } catch (err) {
      toast.error(err?.response?.data?.message || t('provider.beforeAfter.toasts.resubmitFailed'));
    }
  };

  const filtered = stateFilter === 'all' ? cases : cases.filter((c) => c.state === stateFilter);

  return (
    <div className="min-h-screen bg-slate-50">
      <Toaster />
      <SendConsentRequestModal
        open={sendReqOpen}
        onClose={() => { setSendReqOpen(false); fetchCases(); }}
      />
      <div className="max-w-6xl mx-auto px-6 py-8">
        <Button variant="ghost" size="sm" onClick={() => navigate('/clinic/admin')} className="mb-2 -ml-3" data-testid="back-to-dashboard">
          <ArrowLeft className="h-4 w-4 mr-1" /> {t('provider.beforeAfter.back')}
        </Button>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-semibold text-slate-900 flex items-center gap-2">
              <ShieldCheck className="h-7 w-7 text-emerald-600" />
              {t('provider.beforeAfter.title')}
            </h1>
            <p className="text-sm text-slate-600 mt-1 max-w-2xl">
              {t('provider.beforeAfter.subtitle')}
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setSendReqOpen(true)}
              data-testid="new-consent-request-btn"
            >
              <Send className="h-4 w-4 mr-2" /> {t('provider.beforeAfter.newConsentRequest')}
            </Button>
            <Button onClick={() => { setCreateOpen(true); setStep(1); }} data-testid="new-case-btn">
              <Plus className="h-4 w-4 mr-2" /> {t('provider.beforeAfter.newCase')}
            </Button>
          </div>
        </div>

        <div className="flex items-center gap-2 mb-4 flex-wrap">
          {['all', 'draft', 'pending_review', 'approved', 'rejected', 'archived'].map((s) => (
            <Button
              key={s}
              variant={stateFilter === s ? 'default' : 'outline'}
              size="sm"
              onClick={() => setStateFilter(s)}
              data-testid={`filter-${s}`}
            >
              {s === 'all' ? t('provider.beforeAfter.filters.all') : stateLabel(s)}
            </Button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-500">
            <Loader2 className="h-5 w-5 mr-2 animate-spin" /> {t('provider.beforeAfter.loading')}
          </div>
        ) : filtered.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center text-slate-600" data-testid="cases-empty">
              <ShieldQuestion className="h-10 w-10 text-slate-300 mx-auto mb-3" />
              <p className="text-base font-medium text-slate-800">{t('provider.beforeAfter.empty.title')}</p>
              <p className="text-sm text-slate-500 mt-1">
                {t('provider.beforeAfter.empty.subtitle')}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="cases-grid">
            {filtered.map((c) => {
              const badge = STATE_BADGE[c.state] || { key: c.state, cls: 'bg-slate-100 text-slate-700' };
              return (
                <Card key={c.case_id} data-testid={`case-${c.case_id}`}>
                  <CardContent className="p-4 space-y-3">
                    <div className="grid grid-cols-2 gap-2">
                      <div className="aspect-square bg-slate-100 rounded overflow-hidden flex items-center justify-center text-slate-400">
                        <AuthMediaThumb mediaId={c.before_media_id} alt={t('provider.beforeAfter.alt.before')} testIdPrefix={`case-${c.case_id}-before`} />
                      </div>
                      <div className="aspect-square bg-slate-100 rounded overflow-hidden flex items-center justify-center text-slate-400">
                        <AuthMediaThumb mediaId={c.after_media_id} alt={t('provider.beforeAfter.alt.after')} testIdPrefix={`case-${c.case_id}-after`} />
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <Badge className={badge.cls}>{stateLabel(c.state)}</Badge>
                      <span className="text-xs font-mono text-slate-400">{c.treatment_slug}</span>
                    </div>
                    {c.caption && <p className="text-sm text-slate-700 line-clamp-2">{c.caption}</p>}
                    {c.state === 'rejected' && c.rejection_reason && (
                      <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2">
                        <strong>{rejectionLabel(c.rejection_reason)}</strong>
                        {c.rejection_note ? ` — ${c.rejection_note}` : ''}
                      </div>
                    )}
                    <div className="flex items-center gap-2 pt-1">
                      {c.state === 'rejected' && (
                        <Button size="sm" variant="outline" onClick={() => resubmit(c.case_id)} data-testid={`resubmit-${c.case_id}`}>
                          <Send className="h-3.5 w-3.5 mr-1" /> {t('provider.beforeAfter.actions.resubmit')}
                        </Button>
                      )}
                      {c.state !== 'archived' && (
                        <Button size="sm" variant="ghost" onClick={() => archiveCase(c.case_id)} data-testid={`archive-${c.case_id}`}>
                          <Archive className="h-3.5 w-3.5 mr-1" /> {t('provider.beforeAfter.actions.archive')}
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {createOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="create-case-modal">
          <Card className="w-full max-w-xl max-h-[90vh] overflow-y-auto">
            <CardContent className="p-6 space-y-5">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold">{t('provider.beforeAfter.modal.titleTemplate', { step })}</h2>
                <Button variant="ghost" size="sm" onClick={() => setCreateOpen(false)} disabled={busy} data-testid="close-modal">
                  <X className="h-4 w-4" />
                </Button>
              </div>

              {step === 1 && (
                <>
                  <div>
                    <Label>{t('provider.beforeAfter.modal.treatment')}</Label>
                    <select
                      value={form.treatment_slug}
                      onChange={(e) => setForm({ ...form, treatment_slug: e.target.value })}
                      className="w-full mt-1 rounded-md border border-slate-300 px-3 py-2"
                      data-testid="select-treatment"
                    >
                      <option value="">{t('provider.beforeAfter.modal.treatmentPlaceholder')}</option>
                      {treatments.map((tr) => (
                        <option key={tr.treatment_slug} value={tr.treatment_slug}>
                          {tr.master?.name || tr.treatment_slug}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <Label>{t('provider.beforeAfter.modal.consentPath')}</Label>
                    {/* TREATX-L1E: `provider_attested` bypasses patient
                        exact-media consent. It is retained in the
                        backend for legacy audit reads but is NOT
                        exposed in the new wizard. Only the token-based
                        patient-self flow may create new cases. */}
                    <div className="grid grid-cols-1 gap-2 mt-1">
                      {['patient_self'].map((p) => (
                        <button
                          key={p}
                          type="button"
                          onClick={() => setForm({ ...form, consent_path: p, patient_consent_id: '' })}
                          className={`rounded-md border px-3 py-3 text-left text-sm ${form.consent_path === p ? 'border-emerald-500 bg-emerald-50' : 'border-slate-300 bg-white'}`}
                          data-testid={`consent-path-${p}`}
                        >
                          <div className="font-medium">
                            {t('provider.beforeAfter.modal.consentSelfLabel')}
                          </div>
                          <div className="text-xs text-slate-500 mt-1">
                            {t('provider.beforeAfter.modal.consentSelfDesc')}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {form.consent_path === 'patient_self' ? (
                    <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                      <strong>{t('provider.beforeAfter.modal.consentActionTitle')}</strong>
                      <p className="mt-1">
                        {t('provider.beforeAfter.modal.consentActionDesc')}
                      </p>
                      <Input
                        placeholder={t('provider.beforeAfter.modal.consentIdPlaceholder')}
                        value={form.patient_consent_id}
                        onChange={(e) => setForm({ ...form, patient_consent_id: e.target.value })}
                        className="mt-2 bg-white"
                        data-testid="input-consent-id"
                      />
                    </div>
                  ) : null}
                  <div className="flex justify-end">
                    <Button onClick={() => setStep(2)} disabled={!form.treatment_slug || !form.patient_consent_id} data-testid="step1-next">
                      {t('provider.beforeAfter.actions.next')}
                    </Button>
                  </div>
                </>
              )}

              {step === 2 && (
                <>
                  <p className="text-sm text-slate-600">{t('provider.beforeAfter.modal.beforeAfterHint')}</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="rounded-md border border-dashed border-slate-300 p-3 text-center">
                      <input ref={beforeRef} type="file" accept="image/*" hidden onChange={(e) => onFile(e, 'before')} />
                      <ImageIcon className="h-8 w-8 mx-auto text-slate-400" />
                      <Button variant="outline" size="sm" className="mt-2" onClick={() => beforeRef.current?.click()} disabled={busy} data-testid="upload-before">
                        <Upload className="h-4 w-4 mr-2" /> {t('provider.beforeAfter.modal.beforeBtn')}
                      </Button>
                      {form.before_media_id && <p className="text-xs text-emerald-700 mt-1">{t('provider.beforeAfter.modal.uploadedTick')}</p>}
                    </div>
                    <div className="rounded-md border border-dashed border-slate-300 p-3 text-center">
                      <input ref={afterRef} type="file" accept="image/*" hidden onChange={(e) => onFile(e, 'after')} />
                      <ImageIcon className="h-8 w-8 mx-auto text-slate-400" />
                      <Button variant="outline" size="sm" className="mt-2" onClick={() => afterRef.current?.click()} disabled={busy} data-testid="upload-after">
                        <Upload className="h-4 w-4 mr-2" /> {t('provider.beforeAfter.modal.afterBtn')}
                      </Button>
                      {form.after_media_id && <p className="text-xs text-emerald-700 mt-1">{t('provider.beforeAfter.modal.uploadedTick')}</p>}
                    </div>
                  </div>
                  <div className="flex justify-between">
                    <Button variant="outline" onClick={() => setStep(1)} disabled={busy}>{t('provider.beforeAfter.actions.back')}</Button>
                    <Button onClick={() => setStep(3)} disabled={!form.before_media_id || !form.after_media_id} data-testid="step2-next">{t('provider.beforeAfter.actions.next')}</Button>
                  </div>
                </>
              )}

              {/* TREATX-L1E — Step 3: regulatory dates + provider attestations */}
              {step === 3 && (
                <>
                  <div className="space-y-3">
                    <h3 className="text-sm font-semibold text-slate-900">
                      {t('provider.beforeAfter.l1e.headingDates')}
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div>
                        <Label htmlFor="procedure-date">{t('provider.beforeAfter.l1e.fields.procedureDate')}</Label>
                        <Input
                          id="procedure-date"
                          type="date" lang={dateInputLang()}
                          value={form.procedure_date}
                          onChange={(e) => setForm({ ...form, procedure_date: e.target.value })}
                          data-testid="input-procedure-date"
                        />
                      </div>
                      <div>
                        <Label htmlFor="before-captured-at">{t('provider.beforeAfter.l1e.fields.beforeCapturedAt')}</Label>
                        <Input
                          id="before-captured-at"
                          type="date" lang={dateInputLang()}
                          value={form.before_captured_at}
                          onChange={(e) => setForm({ ...form, before_captured_at: e.target.value })}
                          data-testid="input-before-captured-at"
                        />
                      </div>
                      <div>
                        <Label htmlFor="after-captured-at">{t('provider.beforeAfter.l1e.fields.afterCapturedAt')}</Label>
                        <Input
                          id="after-captured-at"
                          type="date" lang={dateInputLang()}
                          value={form.after_captured_at}
                          onChange={(e) => setForm({ ...form, after_captured_at: e.target.value })}
                          data-testid="input-after-captured-at"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2 rounded-md border border-slate-200 p-3">
                    <h3 className="text-sm font-semibold text-slate-900">
                      {t('provider.beforeAfter.l1e.headingAttestations')}
                    </h3>
                    {[
                      ['same_environment_confirmed', 'sameEnvironment'],
                      ['no_retouch_confirmed', 'noRetouch'],
                      ['no_privacy_violation_confirmed', 'noPrivacyViolation'],
                      ['no_incentive_offered_confirmed', 'noIncentive'],
                    ].map(([field, key]) => (
                      <label
                        key={field}
                        className="flex items-start gap-2 text-sm text-slate-800 cursor-pointer"
                        data-testid={`attest-label-${field}`}
                      >
                        <input
                          type="checkbox"
                          checked={!!form[field]}
                          onChange={(e) => setForm({ ...form, [field]: e.target.checked })}
                          className="mt-0.5"
                          data-testid={`attest-${field}`}
                        />
                        <span>{t(`provider.beforeAfter.l1e.attestations.${key}`)}</span>
                      </label>
                    ))}
                  </div>

                  <div className="flex justify-between">
                    <Button variant="outline" onClick={() => setStep(2)} disabled={busy}>{t('provider.beforeAfter.actions.back')}</Button>
                    <Button
                      onClick={() => setStep(4)}
                      disabled={
                        !form.procedure_date || !form.before_captured_at || !form.after_captured_at
                        || !form.same_environment_confirmed || !form.no_retouch_confirmed
                        || !form.no_privacy_violation_confirmed || !form.no_incentive_offered_confirmed
                      }
                      data-testid="step3-next"
                    >
                      {t('provider.beforeAfter.actions.next')}
                    </Button>
                  </div>
                </>
              )}

              {step === 4 && (
                <>
                  <div>
                    <Label>{t('provider.beforeAfter.modal.captionLabel')}</Label>
                    <Textarea
                      rows={3}
                      maxLength={500}
                      value={form.caption}
                      onChange={(e) => setForm({ ...form, caption: e.target.value })}
                      placeholder={t('provider.beforeAfter.modal.captionPlaceholder')}
                      data-testid="input-caption"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      {t('provider.beforeAfter.modal.captionHelper')}
                    </p>
                  </div>
                  <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 flex gap-2">
                    <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0" />
                    <span>
                      {t('provider.beforeAfter.modal.reviewWarning')}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <Button variant="outline" onClick={() => setStep(3)} disabled={busy}>{t('provider.beforeAfter.actions.back')}</Button>
                    <Button onClick={submitCase} disabled={busy} data-testid="submit-case-btn">
                      {busy
                        ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {t('provider.beforeAfter.actions.submitting')}</>
                        : <><Send className="h-4 w-4 mr-2" /> {t('provider.beforeAfter.actions.submit')}</>}
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default BeforeAfterCasesPage;
// Silence unused constant warning: REJECTION_KEY documents canonical enum.
export { REJECTION_KEY };
